// Touch-friendly long-press helpers for the Booking Calendar.
//
// HTML5 DnD needs a mouse — right-click and drag-and-drop don't fire on
// tablets or phones. To keep the calendar usable on an iPad the operator
// can "tap and hold" on an appointment tile (or lunch block) to open the
// same context menu they'd get from a desktop right-click.
//
// Behaviour:
//   • 500 ms of stationary touch fires `onLongPress(x, y)`.
//   • Any finger movement > 10 px cancels the timer (so a scroll or a
//     touch-drag never accidentally opens the menu).
//   • The synthetic `click` that fires after `touchend` on iOS is
//     swallowed by the caller reading `consumedLongPress(state, key)`
//     inside its own onClick handler — that way long-press ⇒ menu,
//     short tap ⇒ normal click (open details), without conflict.

export function makeLongPressState() {
  return { timer: null, x: 0, y: 0, firedFor: null };
}

/**
 * Build the spreadable set of touch handlers for one element.
 *
 * @param {ReturnType<typeof makeLongPressState>} state  Shared per-grid state
 * @param {string} key                                    Stable id for this element
 * @param {(x:number, y:number) => void} onLongPress     Callback fired after the hold
 * @param {{delay?:number, moveThreshold?:number}=} opts Override defaults
 */
export function longPressHandlers(state, key, onLongPress, opts = {}) {
  const delay = opts.delay ?? 500;
  const moveThreshold = opts.moveThreshold ?? 10;
  return {
    onTouchStart: (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      if (state.timer) clearTimeout(state.timer);
      const t = e.touches[0];
      state.x = t.clientX;
      state.y = t.clientY;
      state.firedFor = null;
      state.timer = setTimeout(() => {
        state.timer = null;
        state.firedFor = key;
        onLongPress(state.x, state.y);
      }, delay);
    },
    onTouchMove: (e) => {
      if (!state.timer || !e.touches || !e.touches[0]) return;
      const t = e.touches[0];
      if (Math.hypot(t.clientX - state.x, t.clientY - state.y) > moveThreshold) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    },
    onTouchEnd: () => {
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    },
    onTouchCancel: () => {
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    },
  };
}

/**
 * Consume the "long-press fired" flag for a target. Call from the same
 * element's onClick to suppress the synthetic click that iOS fires right
 * after touchend.
 *
 * @returns {boolean} true when this click should be treated as already
 *                    handled by the long-press (caller should return early)
 */
export function consumedLongPress(state, key) {
  if (state.firedFor === key) {
    state.firedFor = null;
    return true;
  }
  return false;
}
